<!-- =========================================================================================
    Base cho ô number input
    Bao gồm các tính năng:
      + Tự động fomat loại tiền tệ, số lượng (có ngăn cách giữa các số phần nguyên và phân tách phần nguyên và phần thập phân)
      + Số tự động căn phải
      + Auto foucus
    Created by nnanh2 - 24/06/2019
========================================================================================== -->
<template>
  <div class="ms-number ms-editor w-100" :class="[$attrs.class, {'ms-validate': isValidate}]">
    <label v-if="label" class="label">{{ label }}</label>

    <div class="flex-row" :class="[{error: errorMessage, disabled: disabled}, hasBorder ? 'border' : '']">
      <slot name="icon-left">
      </slot>
      <div
        class="w-100 custom-text"
        :class="[
          customTextLeft || customTextRight ? 'flex-row flex-center' : '',
          customTextRight ? 'pr-1' : '',
          customTextLeft ? 'pl-1' : '',
        ]"
      >
        <span class="text-left w-space-no-wrap" v-if="customTextLeft">
          {{ customTextLeft }}
        </span>
        <input
          ref="input"
          :placeholder="placeholder"
          :autofocus="autofocus"
          :step="step"
          :readOnly="readOnly"
          :class="['ms-number-item w-100', customTextRight ? 'cus-txt-r' : '', customClassInput]"
          :disabled="disabled"
          :tabindex="0"
          v-on="listeners"
          @keydown="upDownValue"
          @blur="onActionBlur"
        />
        <span class="text-left w-space-no-wrap" v-if="customTextRight">
          {{ customTextRight }}
        </span>
      </div>

      <div v-if="spin" class="ms-number-spin flex-column">
        <div class="flex spin up" @click="plus()"></div>
        <div class="flex spin down" @click="less()"></div>
      </div>
    </div>
    <div v-if="errorMessage" class="error-text">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import AutoNumeric from '@/libs/autonumeric';
import msBaseComponent from '@/components/base/MsBaseComponent.vue';
import {useValidateControl} from '@/composables/validateControl.js';
import {ModuleContext} from '@/stores/moduleConst';
import {useContextStore} from '@/stores/context';
import {getCurrentInstance} from '@vue/runtime-core';
// import store from '@/stores/store';
export default {
  name: 'MsNumber',
  extends: msBaseComponent,
  props: {
    modelValue: {
      default: '',
      type: [String, Number],
    },
    /**
     * Cấu hình cho icon bên phải
     */
    hasCellButtons: {
      default: false,
      type: Boolean,
    },
    /**
     * Hiện thị chũ mờ ở dưới ô input
     */
    placeholder: {
      default: null,
      type: [Number, String],
    },
    /**
     * Giá trị nhỏ nhất cho phép nhập
     */
    min: {
      default: -99999999999999,
      type: [Number, String],
    },
    /**
     * Giá trị lớn nhất cho phép nhập
     */
    max: {
      default: 99999999999999,
      type: [Number, String],
    },
    /**
     * Tự động focus
     */
    autofocus: {
      default: false,
      type: [Boolean, String],
    },
    /**
     * Bước nhảy của giá trị
     */
    step: {
      default: 1,
      type: [Number, String],
    },
    /**
     * Chiều cao của input
     */
    height: {
      // chiều cao của ô input
      default: 30,
      type: [Number, String],
    },
    /**
     * Kích thước đường viền
     */
    borderSize: {
      default: '1px',
      type: String,
    },
    /**
     * Tiêu đề của control
     */
    title: {
      type: String,
    },
    /**
     * Diễn giải thêm cho control
     */
    tooltip: {
      type: String,
    },
    /**
     * Title dành riêng cho việc xử lý validation trên grid (do không muốn show title lên)
     */
    titleValidation: {
      type: String,
    },
    /**
     * Trường bắt buộc nhập
     * Mặc định false
     */
    required: {
      type: Boolean,
      default: false,
    },
    /**
     * Kí hiệu cảnh báo trường bắt buộc nhập không được bỏ trống
     */
    textRequired: {
      type: String,
      default: '*',
      required: false,
    },
    /**
     * Có hiển thị ký tự required ra không
     */
    requiredVisible: {
      type: Boolean,
      default: false,
    },
    /**
     * Các rule phục vụ cho validate control
     */
    rules: {
      type: [Object, String],
      default: '',
    },
    /**
     * id của validator provider
     */
    vid: {
      type: String,
    },
    /**
     * Cho phép hiển thị số 0 ở đầu
     * [`allow`, `deny`, `keep`]
     * Mặc định là allow: Cho phép nhập nhưng sẽ mất khi focus ra bên ngoài
     */
    leadingZero: {
      type: String,
      default: 'allow',
    },
    /**
     * Cờ bật tắt icon tăng giảm giá trị của number
     */
    spin: {
      type: Boolean,
      default: false,
    },
    /**
     * Format giá trị theo tùy chọn của hệ thống
     */
    formatType: {
      type: [Number, String],
      default: 0,
    },
    /**
     * Cho phép hiển thị đầy đủ số lượng phần thập phân
     */
    allowDecimalPadding: {
      type: Boolean,
      default: true,
    },
    /**
     * Cho phép phân tách các kí tự phần nguyên với nhau
     */
    allowDigitGroupSeparator: {
      type: Boolean,
      default: true,
    },
    /**
     * Set giá trị default của number khi người dùng không set giá trị
     */
    emptyInput: {
      type: Boolean,
      default: false,
    },
    /**
     * Cho phép có giá trị là null/undefined mà không phải set mặc định là 0
     * @author NVLAM 28.02.2020
     */
    isNullable: {
      type: Boolean,
      default: true,
    },
    decimalPlaces: {
      default: 0,
      type: Number,
    },
    /*
     * Hàm show ra câu thông báo riêng theo từng trường hợp
     * pvduy 19/05/2021
     * ví dụ: customWarring {rules:'required', mes: 'Tài sản này là của pvduy'}
     */
    customWarring: {
      type: Object,
      default: null,
    },
    // Xác định xem input có border hay không?
    hasBorder: {
      default: true,
      type: Boolean,
    },
    customTextLeft: {
      default: null,
      type: String,
    },
    customTextRight: {
      default: null,
      type: String,
    },
    customClassInput: {
      default: null,
      type: String,
    },
    /**
     * base cấu hình decimalPlaces sai không thể dùng được
     * Bổ sung thêm cấu hình để sử dụng
     */
    decimalPlacesCustom: {
      default: -1,
      type: Number,
    },
    /**
     * Chặn event paste
     */
    preventPaste: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'msKeydown', 'onActionBlur', 'focus'],
  setup(props) {
    const {errorMessage, validate, isValidate} = useValidateControl({
      props,
    });

    const {proxy} = getCurrentInstance();
    /**
     * Xử lý paste value
     */
    function pasteInput(e) {
      if (props.preventPaste) {
        e.preventDefault();
      }
    }

    return {
      errorMessage,
      validate,
      isValidate,
      pasteInput
    };
  },

  data() {
    return {
      focused: false,
      // numberInput: this.modelValue == null?'':this.modelValue.toString(),
      // autoNumeric: null,
      // width: '',
    };
  },

  computed: {
    listeners() {
      return {
        // ...this.$listeners,
        input: evt => {
          this.updateVModel();
          this.onInput();
        },

        keydown: event => {
          this.$emit(this.$ms.constant.CustomEvent.MsKeydown, event, this.modelValue);
        },
        focus: event => {
          this.selectOnClick = true;
          // pvduong1 - 06/04/2020 - Khi focus vào thì select all dữ liệu trong controll
          event.target.select();
          this.focused = true;
          this.$emit('focus', event);
        },
        blur: event => this.onBlur(event),
        paste: e => {
          this.pasteInput(e);
        },
      };
    },
    boderInput() {
      return {
        border: `${this.borderSize} solid rgb(186, 190, 197)`,
      };
    },

    /**
     * @return {Object}
     */
    anInfo() {
      return {
        value: this.modelValue,
        numberFormat: this.$ms.session,
      };
    },
  },

  watch: {
    /**
     * Sửa lại watch theo value theo hướng dẫn của anh bnduc
     * @author pcminh 13.05.2020
     * HTHIEP 25.01.2022: migration sang vue 3
     */
    modelValue(newValue, oldValue) {
      const me = this;
      if (
        // Make sure this is only called when the value is set by an external script, and not from a user input
        me.getValue() !== newValue &&
        // Compare the 'newValue' with the current 'oldValue' so we do not `set` it again if it's not needed
        newValue !== oldValue
      ) {
        // The modification comes from a script, so we need to reformat the new value `newValue.value`
        if (!newValue || newValue === 0) {
          me.autoNumeric.clear();
        } else {
          me.autoNumeric.set(newValue);
        }
      }
    },
  },

  mounted() {
    // Initialize the autoNumeric element

    const me = this;
    const options = me.initOptions();
    if (me.emptyInput) {
      options.emptyInputBehavior = 'zero';
    }
    // bỏ đi để control trên grid khi alt+tab xem số liệu ở chỗ khác quay lại thì bị select all text
    options.selectOnFocus = false;
    me.autoNumeric = new AutoNumeric(me.$refs.input, options);
    // Bỏ event paste của autoNumeric
    if (me.preventPaste) {
      me.autoNumeric?.domElement?.removeEventListener('paste', me.autoNumeric?._onPasteFunc, false);
    }
    if (this.modelValue !== null && this.modelValue !== '' && typeof this.modelValue !== 'undefined') {
      if (this.modelValue > this.max || this.modelValue < this.min) {
        this.autoNumeric.set(this.max);
      } else {
        this.autoNumeric.set(this.modelValue);
      }
      // The `v-model` must be updated with that default value on startup
      this.updateVModel();
    }
  },

  /**
   * Chuyển một số biến không cần reactive vào đây để tối ưu
   */
  created() {
    const me = this;

    /**
     * Cờ xác định có phải người dùng đang thực hiện typing trên control hay không
     */
    me.typing = false;
    me.valid = false;
    me.autoNumeric = null;
  },
  methods: {
    // Sự kiện blur
    blurHandler(valid, errors) {
      if (!valid) {
        this.$emit('validationError', errors);
      }
      this.changeFocus(false);
    },

    /**
     * Sự kiện blur
     * NXCHIEN 17.05.2022
     */
    onActionBlur() {
      this.$emit('onActionBlur', this.modelValue);
    },
    /**
     * Thay đổi trạng thái focus
     */
    changeFocus(booleanx) {
      this.focused = booleanx;
    },
    /**
     * Thực hiện focus vào ô input number
     */
    focusInput() {
      this.$refs.input.focus();
    },
    /**
     * Sau sẽ bổ sung thêm DataType (Số tiền, Số lượng, tỷ lệ,...) để xác định đúng định dạng
     * Việc khởi tạo này sau này sẽ để 1 object mặc định trên session để tối ưu
     */
    initOptions() {
      const me = this;
      const context = useContextStore();
      const numberFormat = context.NumberFormat;
      const options = {
        allowDecimalPadding: me.allowDecimalPadding, // Cho phép hiển thị đầy đủ số lượng phần thập phân
        decimalCharacter: numberFormat.GeneralDecimalSeparator, // Kí tự phân tách phần nguyên với phần thập phân
        digitGroupSeparator: numberFormat.GeneralGroupSeparator, // Kí tự phân tách phần nguyên với nhau
        decimalCharacterAlternative: '.',
        negativePositiveSignPlacement: 'p', // set vị trí dấu âm: [p - prefix, s - suffix, r - right, l - left]
        // emptyInputBehavior : 'zero', //set giá trị hiển thị mặc định khi number ko có giá trị là 0
        modifyValueOnWheel: 'false', // hỗ trợ có lăn chuột thay đổi giá trị hay không
        // currencySymbol: sessionObj.CurrencySymbol,
        currencySymbolPlacement: 's', // vị trị đặt dấu tiền tệ
        // roundingMethod: 'U',
        maximumValue: me.max,
        minimumValue: me.min,
        leadingZero: me.leadingZero, // cho phép giữ số 0 ở đầu
      };
      if (me.allowDigitGroupSeparator === false) {
        options.digitGroupSeparator = '';
      }
      if (me.decimalPlaces) {
        options.decimalPlaces = me.decimalPlaces || 0;
      } else {
        switch (me.formatType) {
          case me.$ms.constant.FormatType.Amount:
            options.decimalPlaces = numberFormat.AmountDecimalDigits;
            break;
          case me.$ms.constant.FormatType.Allocation:
            options.decimalPlaces = numberFormat.AllocationDecimalDigits;
            break;
          case me.$ms.constant.FormatType.AmountOC:
            options.decimalPlaces = numberFormat.AmountOCDecimalDigits;
            break;
          case me.$ms.constant.FormatType.ExchangeRate:
            options.decimalPlaces = numberFormat.ExchangRateDecimalDigits;
            break;
          case me.$ms.constant.FormatType.Quantity:
            options.decimalPlaces = numberFormat.QuantityDecimalDigits;
            break;
          case me.$ms.constant.FormatType.UnitPrice:
            options.decimalPlaces = numberFormat.UnitPriceDecimalDigits;
            break;
          case me.$ms.constant.FormatType.UnitPriceOC:
            options.decimalPlaces = numberFormat.UnitPriceOCDecimalDigits;
            break;
          case me.$ms.constant.FormatType.Percentage:
            options.decimalPlaces = numberFormat.CoefficientDecimalDigits;
            if(me.decimalPlacesCustom != -1){
              options.decimalPlaces = me.decimalPlacesCustom;
            }
            break;
          default:
            options.decimalPlaces = 0;
            if(me.decimalPlacesCustom != -1){
              options.decimalPlaces = me.decimalPlacesCustom;
            }
            break;
        }
      }

      return options;
    },

    /**
     * Update the v-model value and make the parent aware of that change.
     *
     * @param {Event} event This is needed if we want to use the `event.timeStamp` attribute
     */
    updateVModel(event) {
      const me = this;
      if (me.autoNumeric != null) {
        const value = me.getValue();
        me.$emit('update:modelValue', value, event);
      }
    },

    upDownValue(e) {
      const me = this;
      if (me.spin) {
        if (event.keyCode === 38) {
          event.preventDefault();
          this.plus();
        }
        if (event.keyCode === 40) {
          event.preventDefault();
          this.less();
        }
      } else {
        if (me.isGridEditor) {
          const el = me.$refs.input;
          if (el.selectionStart == 0 && el.selectionEnd == el.value.length && (e.which == 39 || e.which == 37)) {
            const readOnly = el.readOnly;
            if (!el.readOnly) {
              el.readOnly = true;
            }
            setTimeout(function () {
              el.readOnly = readOnly;
            });
          }
        }
      }
    },
    plus() {
      // disable thì không làm gì
      if (this.disabled) {
        return;
      }

      this.changeFocus(true);
      const me = this;
      let value = me.getValue();
      if (!value) {
        value = 0;
      }
      value = parseInt(value);
      if (value < me.max) {
        value += me.step;
        // if (value < 10) {
        // 	value = '0' + value;
        // }
        me.autoNumeric.set(value);
        me.updateVModel();
        me.$nextTick(() => {
          this.$emit('change', me.getValue(), event);
        });
      }
    },
    less() {
      // disable thì không làm gì
      if (this.disabled) {
        return;
      }

      this.changeFocus(true);
      const me = this;
      let value = me.getValue();
      if (!value) {
        value = 0;
      }
      value = parseInt(value);
      if (value > me.min) {
        value -= me.step;
        // if (value < 10) {
        // 	value = '0' + value;
        // }
        me.updateVModel();
        me.$nextTick(() => {
          this.$emit('change', me.getValue(), event);
        });
      }
      me.autoNumeric.set(value);
    },
    /**
     * Lấy giá trị của control
     */
    getValue() {
      const me = this;
      if (!me.isNullable || me.autoNumeric.rawValue) {
        return me.autoNumeric.getNumber();
      }
    },
    /**
     * Gán giá trị vào control
     */
    setInternalValue(value) {
      const me = this;
      me.autoNumeric.set(value);
      me.updateVModel();
    },
    /**
     * Sự kiện người dùng gõ vào ô input (dùng để bật cờ typing)
     */
    onInput() {
      this.typing = true;
    },

    onBlur() {
      const me = this;
      this.typing = false;
      this.validate();
    },
    setValue(value) {
      const me = this;
      if (me.$refs.input) {
        me.$refs.input.value = null;
      }

      if (me.autoNumeric && typeof value !== 'undefined' && value != null) {
        me.setInternalValue(value);
      } else {
        me.innerValue = value;
        this.$emit('update:modelValue', value);
        me.autoNumeric.rawValue = value || null;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
// @media screen and (max-width: 1366px) {
// 	@import '@/assets/scss/_sm-variables.scss';
// 	@import '@/assets/scss/components/number.scss';
// }
// @media screen and (min-width: 1367px) {
//
// 	@import '@/assets/scss/components/number.scss';
// }

@import '@/assets/scss/components/MsNumber.scss';
</style>
